import { useEffect, useRef } from 'react';

/**
 * A still hold on an element that is **also** an activation target (`SUR-26`, ADR-043).
 *
 * Modelled on `app/useCornerLongPress.ts`, and every property copied from it is load-bearing:
 *
 *  - **Still.** Cancelled by movement past {@link MOVE_TOLERANCE_PX}, so a hand resting on the output bar
 *    while the iPad is held does not clear a sentence. The bar is 132 pt tall and 60 % wide in the upper
 *    middle, which is a far likelier resting place than the corner that hook was designed against.
 *  - **One pointer.** `isPrimary` plus `setPointerCapture` on a single `pointerId`, so a second finger
 *    anywhere on the glass cannot cancel a hold in progress or overwrite its timer.
 *  - **No hovering-pencil moves.** `pressure === 0 && buttons === 0` is a pencil or a mouse crossing the
 *    element, not a finger slipping.
 *  - **Cancelled when the app goes away.** A hold resolved by `setTimeout` after a backgrounding would
 *    clear the sentence on return, from a press the person had forgotten making.
 *  - **The trigger through a ref**, so a re-render during the two seconds does not tear the hold down.
 *    That defect was live in the corner hook for months and happened not to fire.
 *
 * ── The one thing deliberately NOT copied ──
 *
 * **No `event.stopPropagation()`.** The corner hook needs it: its element is empty, carries no
 * `data-activate-id`, and in scanning mode an un-swallowed press would double as a switch press. Here the
 * element **is** the activation target, and swallowing its events would leave `DirectTouch`'s
 * `pressTargetId` and `dataset.pressed` set — so the bar stays inverted and the next release anywhere
 * resolves as a miss, emitting an `ActivationMissed` that is calibration's field-loss evidence (§5.5).
 *
 * The double-fire it would have prevented is handled where it belongs instead:
 * `activationRegistry.suppressActivations`. This is the line a reader will copy by reflex, so it is called
 * out rather than left as an absence.
 */

export const HOLD_MS = 2000;
const MOVE_TOLERANCE_PX = 12;

export interface HoldGestureHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

export function useHoldGesture(onHold: () => void, holdMs = HOLD_MS): HoldGestureHandlers {
  const trigger = useRef(onHold);
  trigger.current = onHold;

  const hold = useRef<{
    timer: ReturnType<typeof setTimeout>;
    pointerId: number;
    x: number;
    y: number;
  } | null>(null);

  const cancelHold = (): void => {
    if (hold.current) clearTimeout(hold.current.timer);
    hold.current = null;
  };

  useEffect(() => {
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') cancelHold();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      cancelHold();
      document.removeEventListener('visibilitychange', onHidden);
    };
  }, []);

  const fire = (): void => {
    cancelHold();
    trigger.current();
  };

  return {
    onPointerDown: (event) => {
      if (!event.isPrimary || hold.current) return;

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture is an optimisation, not a requirement; the hold still works without it.
      }

      hold.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        timer: setTimeout(fire, holdMs),
      };
    },

    onPointerMove: (event) => {
      const active = hold.current;
      if (!active || event.pointerId !== active.pointerId) return;
      if (event.pressure === 0 && event.buttons === 0) return;

      const moved =
        Math.abs(event.clientX - active.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - active.y) > MOVE_TOLERANCE_PX;
      if (moved) cancelHold();
    },

    onPointerUp: (event) => {
      if (hold.current && event.pointerId !== hold.current.pointerId) return;
      cancelHold();
    },

    onPointerCancel: (event) => {
      if (hold.current && event.pointerId !== hold.current.pointerId) return;
      cancelHold();
    },
  };
}
